import { CartLinesUpdateEvent } from '@shopify/events';
import { morphSection } from '@theme/section-renderer';

// Uses the same event/promise and section hydration as this theme's cart items.
class KovalaCartRecommendations extends HTMLElement {
  static busy = false;

  constructor() {
    super();
    this.addEventListener('submit', this.onSubmit);
  }

  onSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('.kovala-cart-rec-form')) return;
    event.preventDefault();
    event.stopPropagation();
    if (KovalaCartRecommendations.busy) return;

    const owner = this.closest('cart-items-component');
    if (!owner?.dataset.sectionId) return;
    const button = form.querySelector('button[type="submit"]');
    const status = this.querySelector('.kovala-cart-rec-status');
    const recovery = this.querySelector('.kovala-cart-rec-recovery');
    const fields = new FormData(form);
    const id = String(fields.get('id') || '');
    const quantity = Number(fields.get('quantity'));
    if (!/^\d+$/.test(id) || !Number.isInteger(quantity) || quantity < 1) return;

    const sectionId = owner.dataset.sectionId;
    const sections = [...new Set([sectionId, ...Array.from(
      document.querySelectorAll('cart-items-component[data-section-id]'),
      (element) => element.dataset.sectionId
    )])].slice(0, 5);
    const deferred = CartLinesUpdateEvent.createPromise();
    deferred.promise.catch(() => {});
    const focusedBefore = document.activeElement;
    KovalaCartRecommendations.busy = true;
    const buttons = Array.from(this.querySelectorAll('button[type="submit"]'));
    buttons.forEach((control) => { control.disabled = true; });
    owner.classList.add('cart-items-disabled');
    this.setAttribute('aria-busy', 'true');
    status.textContent = 'Añadiendo…';
    recovery.hidden = true;

    // Dispatch from the owner, exactly as updateQuantity does: it skips its own
    // listener while other cart components and header counters receive the update.
    owner.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add', context: 'cart', lines: [{ id, quantity }], promise: deferred.promise,
    }));

    let mutationStarted = false;
    let added = false;
    let resolved = false;
    let safeToRetry = false;
    try {
      const endpoint = new URL(form.action, window.location.origin);
      endpoint.pathname = endpoint.pathname.replace(/\.js$/, '') + '.js';
      mutationStarted = true;
      const response = await fetch(endpoint, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity }], sections, sections_url: window.location.pathname }),
      });
      const result = await response.json();
      if (!response.ok || result.status >= 400 || result.errors) {
        safeToRetry = response.status >= 400 && response.status < 500;
        throw new Error(typeof result.description === 'string' ? result.description : 'No se ha podido añadir este complemento.');
      }
      added = true;

      const cartResponse = await fetch(`${this.dataset.cartUrl}.js`, {
        credentials: 'same-origin', headers: { Accept: 'application/json' }, cache: 'no-store',
      });
      if (!cartResponse.ok) throw new Error('No se ha podido actualizar el carrito.');
      const cart = await cartResponse.json();
      if (!Array.isArray(cart.items) || !Number.isFinite(cart.item_count)) throw new Error('Respuesta de carrito incompleta.');

      let html = result.sections?.[sectionId];
      if (!html) {
        // A section rendering failure does not mean the add failed; never repeat POST.
        const sectionUrl = new URL(window.location.href);
        sectionUrl.searchParams.set('sections', sectionId);
        const sectionResponse = await fetch(sectionUrl, { credentials: 'same-origin', cache: 'no-store' });
        if (!sectionResponse.ok) throw new Error('No se ha podido actualizar el panel.');
        const rendered = await sectionResponse.json();
        html = rendered[sectionId];
      }
      if (!html) throw new Error('No se ha podido actualizar el panel.');
      const updatedSections = { ...result.sections, [sectionId]: html };

      await morphSection(sectionId, html, { mode: 'hydration' });
      for (const selector of document.querySelectorAll('quantity-selector-component[data-variant-id]')) {
        const item = cart.items.find((line) => String(line.variant_id) === selector.dataset.variantId);
        const input = selector.querySelector('input[data-cart-quantity]');
        if (input) input.setAttribute('data-cart-quantity', String(item?.quantity || 0));
        selector.updateCartQuantity?.();
      }
      for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
        selector.updateButtonStates?.();
      }
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { sections: updatedSections, items: cart.items, itemCount: cart.item_count,
          source: 'kovala-cart-recommendations', didError: false },
      });
      resolved = true;
      const drawer = document.getElementById('cart-drawer');
      const currentStatus = drawer?.querySelector('.kovala-cart-rec-status');
      if (currentStatus) currentStatus.textContent = 'Complemento añadido a tu carrito.';
      if (focusedBefore === button && !button.isConnected && drawer?.querySelector('dialog[open]')) {
        const focusTarget = drawer.querySelector('.kovala-cart-rec-button, .theme-drawer__close-button');
        focusTarget?.focus({ preventScroll: true });
      }
    } catch (error) {
      if (!resolved) deferred.reject(error);
      status.textContent = added
        ? 'El complemento se ha añadido, pero no hemos podido actualizar el panel. Revisa tu carrito antes de volver a añadirlo.'
        : safeToRetry || !mutationStarted
          ? (error.message || 'No se ha podido añadir este complemento.')
          : 'No hemos podido confirmar el resultado. Revisa tu carrito antes de volver a intentarlo.';
      recovery.hidden = safeToRetry;
    } finally {
      KovalaCartRecommendations.busy = false;
      owner.classList.remove('cart-items-disabled');
      document.querySelector('#cart-drawer cart-items-component')?.classList.remove('cart-items-disabled');
      this.removeAttribute('aria-busy');
      // Do not invite duplicate additions after an uncertain network response.
      if (resolved || safeToRetry || !mutationStarted) buttons.forEach((control) => { control.disabled = false; });
    }
  };
}

if (!customElements.get('kovala-cart-recommendations')) {
  customElements.define('kovala-cart-recommendations', KovalaCartRecommendations);
}
